import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SpeakerWaveIcon, XMarkIcon, ArrowUpTrayIcon, Cog6ToothIcon, MusicalNoteIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useDialog } from '../../hooks/useDialog';
import { IAudioGeneration, IUserModel, IProjectAsset, IDialogueTone, IBgm, AssetType, ModelType } from '@packages/global';
import AudioSelector from './AudioSelector';
import DialogueToneSelector from './DialogueToneSelector';
import MusicSelector from './MusicSelector';
import { ProjectAssetSelector } from '../projectAssets';
import { convertAudioToMp3 } from '../../utils/audioConverter';
import AudioPlayer from './AudioPlayer';
import AudioGenerationDialog from '../AudioGeneration/AudioGenerationDialog';
import { useModelStore } from '../../stores/modelStore';
import { useOssDirectUpload } from '../../hooks/useOssDirectUpload';

const WAV_CONVERT_THRESHOLD = 3 * 1024 * 1024; // 3MB

interface AudioUploadProps {
  value?: string;
  onUploadSuccess: (url: string) => void;
  placeholder?: string;
  required?: boolean;
  mode?: 'default' | 'button';
  buttonClassName?: string;
  buttonText?: string;
  projectId?: string;
  episodeId?: string;
  models?: IUserModel[];
  showAudioSelector?: boolean;
  showToneSelector?: boolean;
  showMusicSelector?: boolean;
  showAudioCreator?: boolean;
}

export default function AudioUpload({ value, onUploadSuccess, placeholder, required, mode = 'default', buttonClassName, buttonText = '上传音频', projectId, episodeId, models = [], showAudioSelector = true, showToneSelector = false, showMusicSelector = false, showAudioCreator = false }: AudioUploadProps) {
  const { t } = useTranslation(['common_ui', 'common']);
  const { uploadFile: ossUploadFile, uploading } = useOssDirectUpload();
  const [converting, setConverting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showAudioSelectorModal, setShowAudioSelectorModal] = useState(false);
  const [showToneSelectorModal, setShowToneSelectorModal] = useState(false);
  const [showMusicSelectorModal, setShowMusicSelectorModal] = useState(false);
  const [showAssetSelector, setShowAssetSelector] = useState(false);
  const [showAudioCreatorDialog, setShowAudioCreatorDialog] = useState(false);
  const { loading: modelsLoading, fetchModels, getModelsByType } = useModelStore();
  const { alert } = useDialog();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ttsModels = getModelsByType(ModelType.TEXT_TO_SPEECH);

  // Ensure models are loaded when dialog opens
  useEffect(() => {
    if (showAudioCreator) {
      fetchModels();
    }
  }, [showAudioCreator]);

  const handleAudioCreatorConfirm = (audio: IAudioGeneration) => {
    if (audio.resultAudioUrl) {
      onUploadSuccess(audio.resultAudioUrl);
    }
    setShowAudioCreatorDialog(false);
  };

  const uploadAudio = async (file: File) => {
    // 文件类型预验证
    const allowedAudioTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/aac'];
    if (!allowedAudioTypes.includes(file.type)) {
      await alert(t('common_ui:upload.audio.invalidType'), t('common_ui:upload.audio.uploadFailedTitle'), 'danger');
      return;
    }
    // 文件大小验证（50MB）
    if (file.size > 50 * 1024 * 1024) {
      await alert(t('common_ui:upload.audio.sizeLimit'), t('common_ui:upload.audio.uploadFailedTitle'), 'danger');
      return;
    }

    try {
      // wav 且大于 3MB，先在浏览器端转换为 mp3
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      const needsConvert = ext === '.wav' && file.size > WAV_CONVERT_THRESHOLD;
      if (needsConvert) setConverting(true);
      const fileToUpload = needsConvert
        ? await convertAudioToMp3(file).finally(() => setConverting(false))
        : file;

      const url = await ossUploadFile(fileToUpload);
      onUploadSuccess(url);
    } catch (error) {
      console.error('音频上传失败:', error);
      await alert(t('common_ui:upload.audio.uploadFailed'), t('common_ui:upload.audio.uploadFailedTitle'), 'danger');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadAudio(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    const allowedAudioTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/aac'];
    if (file && allowedAudioTypes.includes(file.type)) {
      uploadAudio(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleAudioSelect = (audio: IAudioGeneration) => {
    if (audio.resultAudioUrl) {
      onUploadSuccess(audio.resultAudioUrl);
      setShowAudioSelectorModal(false);
    }
  };

  const handleToneSelect = (tone: IDialogueTone) => {
    if (tone.previewAudioUrl) {
      onUploadSuccess(tone.previewAudioUrl);
      setShowToneSelectorModal(false);
    }
  };

  const handleMusicSelect = (bgm: IBgm) => {
    if (bgm.audioUrl) {
      onUploadSuccess(bgm.audioUrl);
      setShowMusicSelectorModal(false);
    }
  };

  const handleAssetSelect = (_asset: IProjectAsset, audioUrl: string) => {
    onUploadSuccess(audioUrl);
    setShowAssetSelector(false);
  };

  const clearAudio = () => {
    onUploadSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      {mode === 'button' ? (
        // Button mode - single button only
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            onChange={handleFileSelect}
            className="hidden"
            required={required && !value}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className={buttonClassName || "px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"}
          >
            {uploading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <ArrowUpTrayIcon className="h-4 w-4" />
            )}
            {uploading ? t('common_ui:upload.audio.uploading') : buttonText}
          </button>
        </>
      ) : (
        // Default mode - original layout with selectors
        <>
          {value ? (
            <div className="bg-bg-elevated border border-border-subtle rounded-md p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1">
                  <SpeakerWaveIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  <AudioPlayer src={value} />
                </div>
                <button
                  type="button"
                  onClick={clearAudio}
                  className="text-gray-400 hover:text-gray-200 ml-2 flex-shrink-0"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className={`min-h-[60px] border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors ${dragOver
              ? 'border-indigo-500 bg-indigo-500/10'
              : 'border-gray-600 hover:border-gray-500'
              }`}>
              <div className="flex items-center justify-between gap-3">
                <div
                  className="flex-1 text-left"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    required={required && !value}
                  />

                  {uploading ? (
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mb-2"></div>
                      <p className="text-sm text-gray-400">{converting ? t('common_ui:upload.audio.converting') : t('common_ui:upload.audio.uploading')}</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <div className="flex items-center justify-center">
                        <ArrowUpTrayIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <p className="text-sm text-gray-300">
                          {t('common_ui:upload.audio.dragHint')}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('common_ui:upload.audio.hint')}
                      </p>
                    </div>
                  )}
                </div>

                {(showAudioSelector || showToneSelector || showMusicSelector || projectId) && (
                  <div className='flex flex-col items-center justify-center gap-2 w-16 flex-shrink-0'>
                    {showAudioSelector && (
                      <button
                        type="button"
                        className='w-full py-1 bg-green-700/50 text-white rounded-md hover:bg-green-600/50 transition-colors flex items-center justify-center gap-1'
                        onClick={() => setShowAudioSelectorModal(true)}
                        disabled={uploading}
                      >
                        <SpeakerWaveIcon className="w-3 h-3" />
                        <span className="text-xs">{t('common_ui:upload.audio.audioBtn')}</span>
                      </button>
                    )}
                    {showToneSelector && (
                      <button
                        type="button"
                        className='w-full py-1 bg-blue-700/50 text-white rounded-md hover:bg-blue-600/50 transition-colors flex items-center justify-center gap-1'
                        onClick={() => setShowToneSelectorModal(true)}
                        disabled={uploading}
                      >
                        <SpeakerWaveIcon className="w-3 h-3" />
                        <span className="text-xs">{t('common_ui:upload.audio.toneBtn')}</span>
                      </button>
                    )}
                    {showMusicSelector && (
                      <button
                        type="button"
                        className='w-full py-1 bg-purple-700/50 text-white rounded-md hover:bg-purple-600/50 transition-colors flex items-center justify-center gap-1'
                        onClick={() => setShowMusicSelectorModal(true)}
                        disabled={uploading}
                      >
                        <MusicalNoteIcon className="w-3 h-3" />
                        <span className="text-xs">{t('common_ui:upload.audio.musicBtn')}</span>
                      </button>
                    )}
                    {showAudioCreator && (
                      <button
                        type="button"
                        className='w-full py-1 bg-amber-600/50 text-white rounded-md hover:bg-amber-500/50 transition-colors flex items-center justify-center gap-1'
                        onClick={() => setShowAudioCreatorDialog(true)}
                        disabled={uploading}
                      >
                        <SparklesIcon className="w-3 h-3" />
                        <span className="text-xs">{t('common_ui:upload.audio.createAudio')}</span>
                      </button>
                    )}
                    {projectId && (
                      <button
                        type="button"
                        className='w-full py-1 bg-purple-700/50 text-white rounded-md hover:bg-purple-600/50 transition-colors flex items-center justify-center gap-1'
                        onClick={() => setShowAssetSelector(true)}
                        disabled={uploading}
                      >
                        <Cog6ToothIcon className="w-3 h-3" />
                        <span className="text-xs">{t('common_ui:upload.audio.settingsBtn')}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {showAudioSelector && (
            <AudioSelector
              isOpen={showAudioSelectorModal}
              onClose={() => setShowAudioSelectorModal(false)}
              onSelect={handleAudioSelect}
              models={models}
            />
          )}

          {showToneSelector && (
            <DialogueToneSelector
              isOpen={showToneSelectorModal}
              onClose={() => setShowToneSelectorModal(false)}
              onSelect={handleToneSelect}
            />
          )}

          {showMusicSelector && (
            <MusicSelector
              isOpen={showMusicSelectorModal}
              onClose={() => setShowMusicSelectorModal(false)}
              onSelect={handleMusicSelect}
              showLibrary={false}
            />
          )}

          {projectId && (
            <ProjectAssetSelector
              isOpen={showAssetSelector}
              projectId={projectId}
              episodeId={episodeId}
              onSelect={handleAssetSelect}
              onClose={() => setShowAssetSelector(false)}
              initialAspect="audio"
              initialAssetType={AssetType.CHARACTER}
            />
          )}

          {showAudioCreator && (
            <AudioGenerationDialog
              isOpen={showAudioCreatorDialog}
              onClose={() => setShowAudioCreatorDialog(false)}
              onConfirm={handleAudioCreatorConfirm}
              models={ttsModels}
              modelsLoading={modelsLoading}
            />
          )}
        </>
      )
      }

      {
        placeholder && !value && (
          <p className="text-xs text-gray-500">{placeholder}</p>
        )
      }
    </div >
  );
}
